import { FC, memo } from "react";
import styled from "styled-components";

type CustomProps = {
  spinnerColor?: string;
  fullSize?: boolean;
  fullWindow?: boolean;
  isSmall?: boolean;
};

type LoadingSpinnerProps = CustomProps & {
  className?: string;
};

/**
 * A loading spinner displayed when awaiting a backend response.
 */
const LoadingSpinner: FC<LoadingSpinnerProps> = ({ className, fullSize, fullWindow, ...props }) => {
  const ring = (
    <RingSpinner className={className} {...props}>
      <div />
      <div />
      <div />
      <div />
    </RingSpinner>
  );

  if (fullSize) {
    return <FullSize>{ring}</FullSize>;
  }

  if (fullWindow) {
    return <FullWindow>{ring}</FullWindow>;
  }

  return ring;
};

export const Spinner = () => {
  return (
    <BottomRight>
      <RingSpinner>
        <div />
        <div />
        <div />
        <div />
      </RingSpinner>
    </BottomRight>
  );
};

export default memo(LoadingSpinner);

const FullSize = styled("div")`
  width: 100%;
  height: 100%;
  background: var(var(--modal-overlay-color));
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  z-index: 100;
`;

const FullWindow = styled("div")`
  width: 100vw;
  height: 100vh;
  background: var(var(--modal-overlay-color));
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  z-index: 100;
`;

const BottomRight = styled("div")`
  position: fixed;
  bottom: 34px;
  right: 40px;
  pointer-events: none;
  z-index: 100;
`;

const RingSpinner = styled("div")<CustomProps>`
  display: inline-block;
  position: relative;
  width: ${(p) => (p.isSmall ? "20px" : "40px")};
  height: ${(p) => (p.isSmall ? "20px" : "40px")};

  & div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: ${(p) => (p.isSmall ? "14px" : "32px")};
    height: ${(p) => (p.isSmall ? "14px" : "32px")};
    margin: ${(p) => (p.isSmall ? "3px" : "4px")};
    border: ${(p) => (p.isSmall ? "2px" : "4px")} solid #fff;
    border-radius: 50%;
    animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: ${(p) => p.spinnerColor || "var(--color-navy)"} transparent transparent transparent;
  }

  & div:nth-child(1) {
    animation-delay: -0.45s;
  }

  & div:nth-child(2) {
    animation-delay: -0.3s;
  }

  & div:nth-child(3) {
    animation-delay: -0.15s;
  }

  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
